import './Controls.css'

import React, { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'

import {
  AngleIcon,
  CloseIcon,
  MuteIcon,
  ShareScreenIcon,
  StopIcon,
  UnmuteIcon,
} from '../../../components/Icons'
import { Tooltip } from '../../../components/Tooltip'
import { TooltipHandle } from '../../../components/Tooltip/Tooltip'
import { useSession } from '../../../hooks/storeHooks/useSessionState'
import { Microphone } from '../../../hooks/useMicrophones'
import { SessionMentor } from '../../../types/mentor'
import { analytics } from '../../../utils/analytics'
import { AudioControl } from './AudioControl'
import { UploadInput } from './UploadInput'
import { Wave } from './Wave'

interface IProps {
  isTalking: boolean
  hasNextSpeech: boolean | null
  isMuted: boolean
  controls?: boolean
  personaData: SessionMentor | null
  userMicrophoneStream: MediaStream | null
  selectedMicrophone?: Microphone | null
  contextParam: string | null
  compositeProgress?: number
  audioRef: React.RefObject<HTMLAudioElement | null>
  isAuthenticated: boolean
  estimatedSpeakingTime?: string
  setMuted: (muted: boolean) => void
  setOpenAiVoice: () => void
  handleRepeatCurrentSpeech: () => void
  disableStopButton?: boolean
  disableEndButton?: boolean
  onScreenShare?: () => Promise<void>
  isScreenSharing?: boolean
  showScreenShareButton?: boolean
}
interface LocationState {
  prevLocation?: string
}

export const Controls: React.FC<IProps> = ({
  isTalking,
  hasNextSpeech,
  isMuted,
  controls = true,
  personaData,
  userMicrophoneStream,
  selectedMicrophone,
  contextParam,
  compositeProgress,
  audioRef,
  isAuthenticated,
  estimatedSpeakingTime,
  setMuted,
  setOpenAiVoice,
  handleRepeatCurrentSpeech,
  disableStopButton = false,
  disableEndButton = false,
  onScreenShare,
  isScreenSharing = false,
  showScreenShareButton,
}) => {
  const location = useLocation()
  const state = location.state as LocationState
  const { dataChannel, sessionId } = useSession()
  const showStopButtonTimeout = useRef<NodeJS.Timeout | null>(null)

  const [areControlsVisible, setAreControlsVisible] = useState(controls)
  const [showQuestionUi, setShowQuestionUi] = useState(!!contextParam)
  const [showStopButton, setShowStopButton] = useState(isTalking)
  const [pauseComposite, setPauseComposite] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const tooltipRefs = useRef<Record<string, TooltipHandle>>({})

  const handleMute = () => {
    setMuted(!isMuted)
    analytics.logEvent(
      contextParam
        ? 'watch_session_mute_btn_clicked'
        : 'regular_session_mute_btn_clicked',
    )
  }

  const handleStop = () => {
    const data = {
      type: 'cancel',
      data: { sessionId },
    }
    console.log('stop avatar', data)
    dataChannel?.send?.(JSON.stringify(data))
  }

  const handleQuestionsClick = () => {
    setOpenAiVoice()
    setMuted(false)
    setShowQuestionUi(false)
    analytics.logEvent('watch_session_questions_btn_clicked')
  }

  const handleBackClick = () => {
    setMuted(true)
    setShowQuestionUi(true)
    setPauseComposite(false)
    handleRepeatCurrentSpeech()
    analytics.logEvent('watch_session_back_btn_clicked')
  }

  const handlePauseComposite = () => {
    setPauseComposite(pause => {
      if (pause) {
        handleRepeatCurrentSpeech()
        analytics.logEvent('watch_session_play_btn_clicked')
      } else {
        handleStop()
        analytics.logEvent('watch_session_pause_btn_clicked')
      }
      return !pause
    })
  }

  const handleCloseSession = () => {
    analytics.logEvent(
      contextParam
        ? 'watch_session_end_btn_clicked'
        : 'regular_session_end_btn_clicked',
    )
  }

  const handleShareScreen = async (e: React.MouseEvent) => {
    if (e.currentTarget) {
      ;(e.currentTarget as HTMLButtonElement)?.blur()
    }
    if (!onScreenShare) return

    try {
      await onScreenShare()
      analytics.logEvent('session_share_screen_btn_clicked')
    } catch (error) {
      console.error('Screen sharing failed:', error)
    }
  }

  const handleTrackUploadEvent = () => {
    analytics.logEvent(
      contextParam
        ? 'watch_session_upload_btn_clicked'
        : 'regular_session_upload_btn_clicked',
    )
  }

  useEffect(() => {
    if (!isTalking && !hasNextSpeech && contextParam) {
      setShowQuestionUi(false)
      setMuted(false)
    }
  }, [isTalking, contextParam, contextParam])

  useEffect(() => {
    const handleInteraction = () => {
      if (!controls || showQuestionUi) return
      setAreControlsVisible(true)
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      timeoutRef.current = setTimeout(() => {
        setAreControlsVisible(false)
      }, 5000)
    }

    document.addEventListener('mousemove', handleInteraction)
    document.addEventListener('touchstart', handleInteraction)

    return () => {
      document.removeEventListener('mousemove', handleInteraction)
      document.removeEventListener('touchstart', handleInteraction)
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [showQuestionUi])

  useEffect(() => {
    if (isTalking && !disableStopButton) {
      setShowStopButton(true)
      if (showStopButtonTimeout.current) {
        clearInterval(showStopButtonTimeout.current)
        showStopButtonTimeout.current = null
      }
      return
    }
    showStopButtonTimeout.current = setTimeout(() => {
      setShowStopButton(false)
    }, 300)
  }, [isTalking, disableStopButton])

  const handleTouchEnd = (id: string) => {
    tooltipRefs.current[id]?.closeWithDelay()
  }

  const handleStopTalking = () => {
    handleStop()
    analytics.logEvent('regular_session_stop_talking_btn_clicked')
  }

  return (
    <div
      className={`tc-waveform-wrapper align-middle  ${
        areControlsVisible ? 'expanded' : 'collapsed'
      } ${disableStopButton && disableEndButton ? 'no-end-buttons' : ''}`}
    >
      <div className="tc-chat-question-bar-container align-between">
        {showQuestionUi ? (
          <>
            <Tooltip
              content={pauseComposite ? 'Play' : 'Pause'}
              anchor="#play-pause"
              ref={el => {
                if (el) {
                  tooltipRefs.current['play-pause'] = el
                }
              }}
            />
            <button
              className="tc-waveform-icon w-[40px] align-middle text-white"
              id="play-pause"
              onClick={handlePauseComposite}
              onTouchEnd={() => handleTouchEnd('play-pause')}
            >
              {pauseComposite ? (
                <i className="fa fa-play tc-waveform-icon-pause" />
              ) : (
                <i className="fa fa-pause tc-waveform-icon-pause" />
              )}
            </button>
            <AudioControl audioRef={audioRef} isWatch={!!contextParam} />
            <div className="w-32 h-2 bg-gray-200 rounded overflow-hidden tc-mv-progress hidden md:block">
              <div
                className="h-full bg-white transition-all duration-1000"
                style={{ width: `${compositeProgress}%` }}
              />
            </div>
            {estimatedSpeakingTime && (
              <span className="text-white text-lg whitespace-nowrap">
                {estimatedSpeakingTime}
              </span>
            )}

            <button
              className="tc-waveform-middle flex text-white whitespace-nowrap text-lg gap-2"
              onClick={handleQuestionsClick}
            >
              👋 Questions?
            </button>
          </>
        ) : (
          <>
            {contextParam && hasNextSpeech && (
              <>
                <Tooltip
                  content="Back"
                  anchor="#back-to-composite"
                  ref={el => {
                    if (el) {
                      tooltipRefs.current['back-to-composite'] = el
                    }
                  }}
                />
                <button
                  className="tc-waveform-icon w-[40px] align-middle text-white"
                  onClick={handleBackClick}
                  id="back-to-composite"
                  onTouchEnd={() => handleTouchEnd('back-to-composite')}
                >
                  <AngleIcon />
                </button>
              </>
            )}
            <Tooltip
              ref={el => {
                if (el) {
                  tooltipRefs.current['mute'] = el
                }
              }}
              content={isMuted ? 'Unmute' : 'Mute'}
              anchor="#mute"
            />
            <button
              className="tc-waveform-icon w-[40px] align-middle"
              onClick={handleMute}
              id="mute"
              onTouchEnd={() => handleTouchEnd('mute')}
            >
              {isMuted ? <MuteIcon /> : <UnmuteIcon />}
            </button>
            <AudioControl audioRef={audioRef} isWatch={!!contextParam} />

            <Wave
              userMicrophoneStream={userMicrophoneStream}
              selectedMicrophone={selectedMicrophone}
              isMuted={isMuted}
              isTalking={isTalking}
            />

            {isAuthenticated && (
              <UploadInput
                avatar_id={personaData?.avatar_id || ''}
                onTrackEvent={handleTrackUploadEvent}
              />
            )}

            {/* Share Screen Button */}

            {showScreenShareButton && !contextParam && (
              <>
                <Tooltip
                  content={isScreenSharing ? 'Stop sharing' : 'Share screen'}
                  anchor="#share-screen"
                  ref={el => {
                    if (el) {
                      tooltipRefs.current['share-screen'] = el
                    }
                  }}
                />
                <button
                  className={`tc-waveform-icon w-[40px] align-middle ${
                    isScreenSharing ? 'text-black !bg-white' : 'text-white'
                  }`}
                  id="share-screen"
                  onClick={handleShareScreen}
                  onTouchEnd={() => handleTouchEnd('share-screen')}
                >
                  <ShareScreenIcon />
                </button>
              </>
            )}
            {showStopButton && !disableStopButton && (
              <>
                <Tooltip
                  content="Stop talking"
                  anchor="#stop-talking"
                  ref={el => {
                    if (el) {
                      tooltipRefs.current['stop-talking'] = el
                    }
                  }}
                />
                <button
                  className={`tc-waveform-icon align-middle tc-dark-grey-bg tc-waveform-stop flex-shrink-0 ${
                    isTalking ? 'talking' : ''
                  }`}
                  id="stop-talking"
                  onClick={handleStopTalking}
                  onTouchEnd={() => handleTouchEnd('stop-talking')}
                >
                  <StopIcon />
                </button>
              </>
            )}
          </>
        )}
        {!disableEndButton && (
          <>
            <Tooltip
              content="End call"
              anchor="#end-call"
              ref={(el: TooltipHandle | null) => {
                if (el) {
                  tooltipRefs.current['end-call'] = el
                }
              }}
            />
            <Link
              onTouchEnd={() => handleTouchEnd('end-call')}
              replace
              id="end-call"
              to={state?.prevLocation ? state.prevLocation : '/'}
              className="tc-waveform-icon align-middle tc-dark-grey-bg tc-waveform-call-end flex-shrink-0 text-white w-[40px]"
              onClick={handleCloseSession}
            >
              <CloseIcon />
            </Link>
          </>
        )}
      </div>
    </div>
  )
}
